"use client";

import React, { useEffect, useState } from "react";
import {
  FilterButton,
  SearchInput,
  SummaryCardGrid,
  TabButtonGroup,
  Loader,
} from "../components/shared";
import useMediaQuery from "../utils/hooks/useMediaQuery";
import { useOutletContext } from "react-router-dom";
import { useSelectedCustomer } from "../utils/context/SelectedCustomerContext";
import { voiceCallSummary, buttonLabels } from "../utils/data";
import {
  getAllCustomers,
  formatCurrency,
  getStatusColor,
} from "../utils/data/customerData";
import CustomerImformationOutbound from "../components/voiceCallComp/CustomerImformationOutbound";
import { RotateCcw } from "lucide-react";

const CollectionCampaign = () => {
  const isMdUp = useMediaQuery("(min-width: 768px)");
  const { toggleSidebar } = useOutletContext();
  const { setSelectedCustomerId } = useSelectedCustomer();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(5);
  const { selectedCustomerId } = useSelectedCustomer();
  const [refreshing, setRefreshing] = useState(false);

  const refreshTableData = async () => {
    try {
      setRefreshing(true);

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Get fresh customer data
      const customerList = getAllCustomers();
      setCustomers(customerList);

      // Keep the same selected customer if it still exists
      const currentSelectedCustomer = customerList.find(
        (customer) => customer.customer_id === selectedCustomerId
      );

      // If current customer doesn't exist anymore, select the first one
      if (!currentSelectedCustomer && customerList.length > 0) {
        setSelectedCustomerId(customerList[0].customer_id);
      }
    } catch (error) {
      console.error("Error refreshing customer data:", error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        // Simulate loading time
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Get all customers from static data
        const customerList = getAllCustomers();
        setCustomers(customerList);

        // Automatically select the first customer
        if (customerList.length > 0) {
          setSelectedCustomerId(customerList[0].customer_id);
        }
      } catch (error) {
        console.error("Error fetching customers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [setSelectedCustomerId]);

  const filteredCustomers = customers.filter((customer) => {
    const lowerQuery = searchQuery.toLowerCase();
    return (
      customer.customer_name?.toLowerCase().includes(lowerQuery) ||
      customer.phone_number?.toLowerCase().includes(lowerQuery) ||
      customer.loan_account_id?.toLowerCase().includes(lowerQuery) ||
      customer.product_type?.toLowerCase().includes(lowerQuery)
    );
  });

  if (loading) {
    return (
      <div className="w-full h-[calc(100vh-100px)] flex items-center justify-center">
        <Loader size={40} color="black" />
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full min-h-[calc(100vh-100px)]">
      <div
        className={`flex flex-col ${
          isMdUp ? "w-4/5" : "w-full"
        } h-auto gap-2 lg:gap-2`}
      >
        <div className="w-full h-auto border-b border-[#0000002B] flex justify-between items-center pb-2">
          <h1 className="text-[13px] font-semibold leading-[100%] text-black font-inter">
            Voice Calls
          </h1>
          <div className="flex flex-row items-center gap-2 md:hidden">
            <button
              onClick={toggleSidebar}
              className="px-3 py-1 text-[10px] font-medium text-black border border-[#0000002B] rounded-md hover:bg-black-700 transition"
            >
              Menu
            </button>
          </div>
        </div>

        <TabButtonGroup
          buttonLabels={buttonLabels}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />

        <SummaryCardGrid data={voiceCallSummary} />

        <div className="flex flex-row items-center justify-between w-full pr-1">
          <SearchInput
            placeholder="Search by name, phone, loan ID..."
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <div className="flex flex-row items-center gap-2">
            <button
              onClick={() => console.log("Submit clicked")}
              className="min-w-[80px] h-6 rounded-[7px] px-2 bg-[#E5E6E7] text-black text-[11px] font-inter flex items-center justify-center"
            >
              Initiate Campaign
            </button>
            {/* Refresh Button */}
            <button
              onClick={refreshTableData}
              disabled={refreshing}
              className={`w-6 h-6 rounded-[7px] bg-[#E5E6E7] text-black flex items-center justify-center transition-all duration-200 ${
                refreshing
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-[#D1D5DB]"
              }`}
              title="Refresh table data"
            >
              <RotateCcw
                size={14}
                className={refreshing ? "animate-spin" : ""}
              />
            </button>
            <FilterButton onClick={() => console.log("Filter clicked")} />

            {/* Submit Button */}
          </div>
        </div>

        <div className="flex-1 h-full">
          <div className="flex-1 w-full h-full pr-2">
            <div className="w-full h-full shadow-[4px_4px_47.1px_0px_#00000040] rounded-[10px] px-1 pt-1 overflow-hidden relative">
              {refreshing && (
                <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10 rounded-[10px]">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <RotateCcw size={16} className="animate-spin" />
                    Refreshing...
                  </div>
                </div>
              )}
              <div className="w-full h-[40px] text-center bg-[#F6F9FF] border border-[#00000029] rounded-[10px] flex items-center px-4">
                {[
                  "Customer Info",
                  "Loan ID",
                  "Product",
                  "EMI Due (₹)",
                  "DPD",
                  "Status",
                  "Score Band",
                ].map((col, i) => (
                  <div
                    key={i}
                    className="md:text-[12px] text-[11px] font-normal text-black font-inter w-full"
                  >
                    {col}
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-0 flex-1 mt-2 overflow-y-auto max-h-[380px] 2xl:max-h-[450px] pr-1">
                {filteredCustomers.map((customer, index) => (
                  <React.Fragment key={customer.customer_id}>
                    <div
                      onClick={() =>
                        setSelectedCustomerId(customer.customer_id)
                      }
                      className="w-full cursor-pointer h-[60px] text-center hover:bg-[#F6F9FF] rounded-[12px] flex items-center px-4"
                    >
                      <div className="w-full flex flex-col md:text-[11px] text-[10px] font-inter">
                        <div className="font-medium text-black">
                          {customer.customer_name}
                        </div>
                        <div className="text-black text-opacity-40">
                          {customer.phone_number}
                        </div>
                      </div>
                      <div className="w-full md:text-[11px] text-[10px] font-inter text-opacity-40 text-black">
                        {customer.loan_account_id}
                      </div>
                      <div className="w-full md:text-[11px] text-[10px] font-inter text-opacity-40 text-black">
                        {customer.product_type}
                      </div>
                      <div className="w-full md:text-[11px] text-[10px] font-inter text-opacity-40 text-black">
                        {formatCurrency(customer.emi_due_amount)}
                      </div>
                      <div className="w-full md:text-[11px] text-[10px] font-inter text-opacity-40 text-black">
                        {customer.dpd}
                      </div>
                      <div
                        className="w-full md:text-[11px] text-[10px] font-inter font-medium"
                        style={{
                          color: getStatusColor(customer.delinquency_status),
                        }}
                      >
                        {customer.delinquency_status}
                      </div>
                      <div className="w-full md:text-[11px] text-[10px] font-inter text-opacity-40 text-black">
                        {customer.credit_score_band}
                      </div>
                    </div>
                    <div className="w-full border-b z-10 border-[#00000029]"></div>
                  </React.Fragment>
                ))}

                {filteredCustomers.length === 0 && (
                  <div className="w-full h-[200px] flex items-center justify-center text-gray-500">
                    No customers found matching your search
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar - only on desktop */}
      {isMdUp && <CustomerImformationOutbound />}
    </div>
  );
};

export default CollectionCampaign;
